import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import api from "../api/http";
import type { ApiError } from "../api/http";
import type { MessageResponse } from "../api/types";
import { AuthLayout } from "../components/auth/AuthLayout";
import { PasswordInput } from "../components/auth/PasswordInput";
import {
  authFormError,
  authLabel,
  authLink,
  authMuted,
  authPrimaryBtn,
} from "../components/auth/authStyles";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (!uid || !token) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 ring-1 ring-rose-200 text-rose-600 dark:bg-rose-500/15 dark:ring-rose-500/30 dark:text-rose-300">
            <X size={22} />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            {t("auth.reset.invalidTitle")}
          </h2>
          <p className={`mt-2 text-sm ${authMuted}`}>{t("auth.reset.invalidBody")}</p>
          <Link to="/forgot-password" className={`mt-6 inline-block text-sm ${authLink}`}>
            {t("auth.reset.requestNewLink")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError(t("auth.reset.passwordTooShort"));
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post<MessageResponse>("/api/v1/auth/password-reset-confirm/", {
        uid,
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      const apiError = err as ApiError;
      // 400 = expired/invalid token or a rejected password; the backend detail
      // is English-only, so show the localized catch-all with the expiry hint.
      setError(
        apiError.status === 400
          ? t("auth.reset.failedExpired")
          : t("auth.reset.failedGeneric")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200 text-emerald-600 dark:bg-emerald-500/15 dark:ring-emerald-500/30 dark:text-emerald-300">
            <Check size={22} />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            {t("auth.reset.successTitle")}
          </h2>
          <p className={`mt-2 text-sm ${authMuted}`}>{t("auth.reset.successBody")}</p>
          <Link to="/login" className={`mt-6 inline-block ${authPrimaryBtn} max-w-[12rem]`}>
            {t("auth.reset.signinLink")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.reset.subtitle")}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className={authFormError}>{error}</div>}

        <div>
          <label htmlFor="password" className={authLabel}>
            {t("auth.reset.newPassword")}
          </label>
          <PasswordInput
            id="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth.reset.passwordPlaceholder")}
          />
        </div>

        <button type="submit" disabled={isSubmitting} className={authPrimaryBtn}>
          {isSubmitting ? t("auth.reset.submitting") : t("auth.reset.submit")}
        </button>
      </form>
    </AuthLayout>
  );
}
